<template lang="pug">
div
  div.px-5.py-4
    template(v-if="isFetching")
      v-skeleton-loader(type="heading")
      v-skeleton-loader.mt-3(type="paragraph@2")
    template(v-else)
      div.d-flex.align-center
        div.text-xs.font-semibold.neutral700--text {{ treatment.name }}
        v-spacer
        div.clickable(@click="activeMedication = !activeMedication")
          y-chip.no-wrap(v-if="isMedicine" label="Set as active medicine" size="xs" outlined )
            template(#prepend)
              v-icon.mr-1(v-if="activeMedication" size="15" color="success500") $yzi-check-circle-solid
              v-icon.mr-1(v-else size="15" color="neutral500") mdi-radiobox-blank
        y-button.ml-2(label="Remove" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="danger500" unset-min-width size="xxs" height="24" button-class="px-2" @click="$emit('remove')")
        y-button.ml-2(label="Save" unset-min-width size="xxs" height="24" button-class="px-2" :loading="isLoading" @click="saveData")
      div.mt-2
        v-form(ref="form")
          v-row(dense)
            v-col(cols="2")
              div.text-xs.font-medium.neutral500--text Quantity
              v-text-field.text-xs.round-md.custom-height.mt-1(ref="quantity" v-model="quantity" outlined placeholder="0" type="number" :rules="inventoryEnabled ? rules.allowed_quantity : rules.has_quantity" hide-details dense)
            v-col(v-if="hasUomConversion" cols="2")
              div.text-xs.font-medium.neutral500--text UOM
              medication-unit-of-measure-dropdown.mt-1(v-model="selectedUom" :allowedValues="uomOptions" size="md" :fieldMinWidth="70" placeholder="UOM" :deletable="false")
            v-col(v-else-if="treatment.uom" cols="2")
              div.text-xs.font-medium.neutral500--text UOM
              v-text-field.text-xs.round-md.custom-height.mt-1(:value="treatment.uom" outlined dense hide-details readonly style="background-color: var(--v-neutral300)")
            v-col(v-if="hasPriceTier" :cols="hasUom ? 3 : 4")
              div.text-xs.font-medium.neutral500--text Price tier
              treatment-price-tier-dropdown.mt-1(v-model="priceTier" :item="treatment" size="xs" @change="updateRate($event)")
            v-col(:cols="hasUom ? 2 : 3")
              div.text-xs.font-medium.neutral500--text Rate
              v-text-field.text-xs.round-md.custom-height.mt-1(v-model="rate" outlined placeholder="0" type="number" :rules="rules.must_positive_value" hide-details dense :readonly="!allowedPriceEdit" :style="!allowedPriceEdit ? 'background-color: var(--v-neutral300)' : ''" :prefix="$currency.symbol")
            v-col(:cols="hasUom ? 3 : 3")
              div.text-xs.font-medium.neutral500--text Amount
              v-text-field.text-xs.round-md.custom-height.mt-1(v-model="amount" outlined placeholder="0" type="number" hide-details dense readonly style="background-color: var(--v-neutral300)" :prefix="$currency.symbol")
          v-row(v-if="isMedicine" dense)
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Indication
              treatment-indication-dropdown.mt-1(v-model="indication" size="xs")
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Dosage
              v-row(dense)
                v-col(cols="4")
                  v-text-field.text-xs.round-md.custom-height.mt-1(v-model="dosageValue" outlined placeholder="0" type="number" hide-details dense)
                v-col(cols="8")
                  treatment-dosage-unit-dropdown.mt-1(v-model="dosageUnit" size="xs")
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Frequency
              treatment-frequency-dropdown.mt-1(v-model="frequency" size="xs")
          v-row(v-if="isMedicine" dense)
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Instruction
              treatment-instruction-dropdown.mt-1(v-model="instruction" size="xs")
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Duration
              treatment-duration-dropdown.mt-1(v-model="duration" size="xs")
            v-col(cols="4")
              div.text-xs.font-medium.neutral500--text Precaution
              treatment-precaution-dropdown.mt-1(v-model="precaution" size="xs")
  v-divider
  .dialogs
    add-medical-document-dialog(v-if="showAddMedicalDocumentDialog" :open.sync="showAddMedicalDocumentDialog" :treatment="treatment" :diagnoses="consultation.diagnoses" :new-document="true" @updated="updateDocument")
    y-dialog(v-if="showQuantityError" v-model="showQuantityError" persistent width="315" content-class="round-2xl")
      v-card.pa-6
        div.text-md.font-semibold.text-center Insufficient stock balance
        div.text-sm.text-center.mt-6 You do not have sufficient stock balance for this inventory.
        y-button.mt-6(label="Done" size="sm" block @click="showQuantityError = false")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import AddMedicalDocumentDialog from '@/components/clinic/consultation/AddMedicalDocumentDialog.vue'
import TreatmentPriceTierDropdown from '@/components/clinic/consultation/TreatmentPriceTierDropdown.vue'
import TreatmentDosageUnitDropdown from '@/components/clinic/consultation/TreatmentDosageUnitDropdown.vue'
import TreatmentFrequencyDropdown from '@/components/clinic/consultation/TreatmentFrequencyDropdown.vue'
import TreatmentIndicationDropdown from '@/components/clinic/consultation/TreatmentIndicationDropdown.vue'
import TreatmentInstructionDropdown from '@/components/clinic/consultation/TreatmentInstructionDropdown.vue'
import TreatmentDurationDropdown from '@/components/clinic/consultation/TreatmentDurationDropdown.vue'
import TreatmentPrecautionDropdown from '@/components/clinic/consultation/TreatmentPrecautionDropdown.vue'
import MedicationUnitOfMeasureDropdown from '@/components/clinic/inventory/MedicationUnitOfMeasureDropdown.vue'

export default {
  components: {
    AddMedicalDocumentDialog, TreatmentPriceTierDropdown, TreatmentDosageUnitDropdown, TreatmentFrequencyDropdown,
    TreatmentIndicationDropdown, TreatmentInstructionDropdown, TreatmentDurationDropdown, TreatmentPrecautionDropdown,
    MedicationUnitOfMeasureDropdown
  },
  props: ['treatment'],
  data () {
    return {
      isFetching: false,
      isLoading: false,
      showAddMedicalDocumentDialog: false,
      showQuantityError: false,
      activeMedication: false,
      priceTier: null,
      dosageUnit: null,
      dosageValue: null,
      frequency: null,
      indication: null,
      instruction: null,
      duration: null,
      precaution: null,
      document: null,
      selectedUom: null,
      quantity: 1,
      rate: 1,
      rules: {
        has_quantity: [v => !!v || 'This field is required', v => v > 0 || 'This field is required'],
        must_positive_value: [v => (v != null && v.toString().trim().length) || 'This field is required', v => v >= 0 || 'This field must have positive value'],
        allowed_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required', v => v <= this.inventoryQty || 'Item quantity more than stock on hand'],
      },
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      consultation: 'treatment/getConsultation',
      registration: 'treatment/getRegistration',
      patient: 'treatment/getPatient',
      leadPid: 'treatment/getLeadPid',
      activeMedications: 'treatment/getActiveMedications',
      isDoctor: 'user/isDoctor',
    }),
    hasPriceTier () {
      if (this.treatment.type === 'documents' && !this.treatment.document_template.price_tier_enabled) { return false }
      if (this.treatment.type === 'bundle') { return false }
      return true
    },
    amount () {
      return Number(this.quantity || 0) * Number(this.rate || 0)
    },
    inventoryEnabled () {
      if (this.isPackage) {
        return Boolean(this.treatment.bundle.available_qty)
      }
      return this.treatment.inventory_enabled
    },
    inventoryQty () {
      if (this.isPackage) {
        return this.treatment.bundle.available_qty
      }
      if (this.hasUomConversion && this.selectedUom) {
        let qty = Number(this.treatment.inventory_qty)
        const scale = this.getUomScale(this.selectedUom)
        if (scale > 1) {
          qty = qty / scale
        }
        return qty
      }
      return this.treatment.inventory_qty
    },
    allowedPriceEdit () {
      // admin can edit price
      if (this.isAdmin) return true
      // 'lock_treatment_plan' is for CA
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan') && !this.isAdminOrDoctor) return false
      // 'lock_treatment_plan_price_edit' is for doctor
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan_price_edit') && this.isDoctor) return false
      return true
    },
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
    isMedicine () {
      if (this.treatment.medication && this.treatment.medication.id) {
        return true
      }
      return false
    },
    isDocument () {
      if (this.treatment.type === 'documents') {
        return true
      }
      return false
    },
    isPackage () {
      if (this.treatment.type === 'bundle') {
        return true
      }
      return false
    },
    hasUom () {
      return this.hasUomConversion || !!this.treatment.uom
    },
    hasUomConversion () {
      return this.treatment.uom_conversion_enabled && this.treatment.uom_conversions && this.treatment.uom_conversions.length > 0
    },
    uomOptions () {
      const units = []
      if (this.treatment.uom_conversions) {
        this.treatment.uom_conversions.forEach(c => {
          if (c.uom && !units.includes(c.uom)) units.push(c.uom)
          if (c.convert_to && !units.includes(c.convert_to)) units.push(c.convert_to)
        })
      }
      return units
    },
    isActiveMedication () {
      if (this.treatment.medication && this.treatment.medication.id) {
        if (this.activeMedications.find(f => f.m_id === this.treatment.medication.id)) {
          return true
        }
      }
      return false
    }
  },
  watch: {
    selectedUom () {
      if (this.hasUomConversion && this.priceTier && Array.isArray(this.priceTier.price_per_uom) && this.priceTier.price_per_uom.length) {
        this.updateRate(this.priceTier)
      }
    }
  },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.document = null
      this.selectedUom = this.treatment.uom_conversion_enabled
        ? (this.treatment.uom || (this.treatment.uom_conversions?.length ? this.treatment.uom_conversions[this.treatment.uom_conversions.length - 1].convert_to : null))
        : (this.treatment.uom || null)
      if (this.treatment?.medication?.default_qty) {
        this.quantity = this.treatment.medication.default_qty
      } else {
        this.quantity = 1
      }
      if (this.hasPriceTier) {
        this.updatePriceTier()
      } else {
        this.priceTier = null
        this.rate = (Number(this.treatment.price) / this.$currency.multiplier)
      }
      if (this.isMedicine) {
        this.updateInstructions()
        this.$nextTick(() => {
          this.activeMedication = this.isActiveMedication
        })
      }
      if (this.isDocument) {
        this.showAddMedicalDocumentDialog = true
      }
    },
    updatePriceTier() {
      this.isFetching = true
      let params = { 
        company: this.company.id,
        product: this.treatment.id
      }
      if (this.registration?.default_payment && this.registration.default_payment.type !== 'self-pay') {
        params.payment_methods = this.registration.default_payment.panel.id
      } else {
        params.payment_channel = 'self-pay'
      }
      this.$axios.get('/product_pricetiers', { params })
        .then(resp => {
          if (resp.data.count) {
            this.priceTier =  resp.data.results[0]
            this.updateRate(resp.data.results[0])
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    getUomScale (selectedUom) {
      if (!this.treatment.uom_conversions?.length) return 1
      const conversion = this.treatment.uom_conversions.find(c => c.uom === selectedUom || c.convert_to === selectedUom)
      if (!conversion) return 1
      if (conversion.uom === selectedUom) return conversion.scale
      return 1
    },
    updateRate (pricetier) {
      if (!pricetier || !Object.keys(pricetier).length) return
      let price = Number(pricetier.price || 0)
      if (this.hasUomConversion && Array.isArray(pricetier.price_per_uom) && pricetier.price_per_uom.length && this.selectedUom) {
        const match = pricetier.price_per_uom.find(p => p.uom === this.selectedUom)
        if (match) price = Number(match.price || 0)
      }
      this.rate = price / this.$currency.multiplier
    },
    updateDocument (data) {
      this.document = data
    },
    updateInstructions () {
      if (!this.isMedicine) return
      this.dosageUnit = this.treatment.medication.dosage_unit
      this.dosageValue = this.treatment.medication.dosage_value
      this.frequency = this.treatment.medication.frequency
      this.indication = this.treatment.medication.indication
      this.instruction = this.treatment.medication.instruction
      this.duration = this.treatment.medication.duration
      this.precaution = this.treatment.medication.precaution
    },
    saveData () {
      if (!this.$refs.form.validate()) {
        if (this.$refs.quantity && this.$refs.quantity.hasError) {
          if (this.$refs.quantity.messagesToDisplay[0] == 'Item quantity more than stock on hand') {
            this.showQuantityError = true
            return
          }
        }
        return this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
      } if (this.hasPriceTier && !this.priceTier) {
        return this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
      } if (this.dosageUnit || this.dosageValue) {
        if (!this.dosageUnit || !this.dosageValue) {
          return this.showMessage(['error', 'Please enter the correct dosage amount and unit'])
        }
      }
      this.isLoading = true
      let payload = {
        product: this.treatment.id,
        quantity: this.quantity,
        price: Math.round(Number(this.rate) * this.$currency.multiplier),
        ...(this.treatment.uom_conversion_enabled && this.selectedUom ? { uom: this.selectedUom } : {})
      }
      if (this.priceTier) {
        payload.extra_data = {
          price_tier: this.priceTier.id,
          price_tier_name: this.priceTier.name,
        }
      }
      if (this.isMedicine) {
        payload.extra_data = {
          ...payload.extra_data,
          dosage_unit: this.dosageUnit,
          dosage_value: this.dosageValue,
          frequency: this.frequency,
          duration: this.duration,
          instruction: this.instruction,
          precaution: this.precaution,
          indication: this.indication,
        }
      }
      if (this.isDocument) {
        payload.extra_data = {
          ...payload.extra_data,
          ...this.document
        }
      }
      if (this.isPackage) {
        let bundles = []
        this.treatment.bundle.treatments.forEach(treatment => {
          let item = {
            product: treatment.id,
            quantity_per_set: treatment.quantity_per_set,
          }
          if (treatment.medication) {
            item.extra_data = {
              dosage_unit: treatment.medication.dosage_unit,
              dosage_value: treatment.medication.dosage_value,
              duration: treatment.medication.duration,
              frequency: treatment.medication.frequency,
              indication: treatment.medication.indication,
              instruction: treatment.medication.instruction,
              precaution: treatment.medication.precaution,
            }
          }
          bundles.push(item)
        })
        payload.bundles = bundles
      }
      const endpoint = `/clinics/${this.company.slug}/invoices/${this.leadPid}`
      this.$axios.patch(endpoint, { products: [payload] })
        .then(resp => {
          this.updateActivityLog(endpoint, payload, resp.data, 'POST', resp.data.id, 'consultation', `Update treatment plan (${this.leadPid})`)
          this.updateActiveMedication()
        })
        .catch((err) => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response?.status === 400 && err.response?.data?.code === '444101') {
            message = 'Item quantity more than available stock. Please adjust the quantity'
          }       
          if (err.response.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
          this.isLoading = false
        })
    },
    updateActiveMedication () {
      if (!this.isMedicine) {
        this.finalizeProcess()
        return
      }
      if (this.isActiveMedication !== this.activeMedication) {
        if (this.isActiveMedication) {
          let actmed = this.activeMedications.find(f => f.m_id === this.treatment.medication.id)
          const endpoint = `clinics/${this.company.slug}/active_medications/${actmed.id}`
          this.$axios.delete(endpoint)
            .catch(() => {})
            .finally(() => {
              this.updateActivityLog(endpoint, null, null, 'DELETE', this.treatment.medication.id, 'active_medication', 'deactivate medication')
              this.finalizeProcess()
            })
        } else {
          const endpoint = `clinics/${this.company.slug}/active_medications`
          let payload = {
            medication: this.treatment.medication.id,
            patient: this.patient.id
          }
          this.$axios.post(endpoint, payload)
            .then(resp => {
              this.updateActivityLog(endpoint, payload, resp.data, 'POST', resp.data.id, 'active_medication', 'add active medication')
              this.finalizeProcess()
            })
        }
      } else {
        this.finalizeProcess()
      }
    },
    updateActivityLog (endpoint, payload, response, method, id, objectRef, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: objectRef,
        activity: activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    finalizeProcess () {
      this.showMessage(['success', 'Treatment plan successfully updated'])
      this.$emit('updated')
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined.error--text fieldset {
    border-color: var(--v-danger500) !important;
  }
  .custom-height {
    height: 32px;
    fieldset {
      height: 37px;
    }
    .v-text-field__slot {
      height: 32px;
    }
  }
}
</style>